import { createTheme, type CSSVariablesResolver, type MantineColorsTuple } from '@mantine/core'

/**
 * The half of 07 §4 that is not a token: Mantine paints a disabled input at
 * `opacity: 0.6`, which no CSS custom property can reach. Imported here so the
 * two halves of the disabled-field fix ship together — see `inputs.css` for the
 * measurements and the cascade reasoning.
 */
import './inputs.css'

/**
 * The single Mantine theme — docs/spec/07 §1.
 *
 * Every token lives here; components use theme values, never hard-coded colors
 * or spacings. This is a dense, keyboard-first operations tool for staff doing
 * hundreds of repetitions a day (00 §4), not a marketing site — so the defaults
 * below lean compact and low-chrome on purpose.
 */

/** Pigeonly teal, continuous with the legacy tool's accent (07 §5). */
const teal: MantineColorsTuple = [
  '#e0fbfa',
  '#ccf2f1',
  '#9ce3e0',
  '#68d3cf',
  '#42c6c1',
  '#2abeb8',
  '#14bab3',
  '#00a39d',
  '#00918b',
  '#007d78',
]

export const theme = createTheme({
  primaryColor: 'pigeonlyTeal',
  colors: { pigeonlyTeal: teal },

  /**
   * Let Mantine choose the label color on filled surfaces from the background's
   * luminance, instead of always using white.
   *
   * Pigeonly teal is a **light** accent (`teal[6]` `#14bab3`, luminance 0.385),
   * so Mantine's default white label gave the primary button **2.41:1** — under
   * half of the 4.5 AA needs, on the main call-to-action of every screen
   * (found by the Lighthouse gate, 2026-07-29). With `autoContrast` the label
   * goes dark and the same button measures 8.70:1.
   *
   * Chosen over darkening the palette because it **keeps the brand color
   * exactly** — 07 §5 requires continuity with the legacy tool's accent, and
   * that constrains the background, not the label.
   */
  autoContrast: true,

  // A UI stack, not a display face: this is read all day at small sizes.
  fontFamily:
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  // Parcel codes and ids are compared character-by-character, so they get a
  // monospace face (07 §1).
  fontFamilyMonospace: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',

  headings: { fontWeight: '600' },
  defaultRadius: 'sm',

  components: {
    // Compact density across the data-entry surfaces (07 §1).
    Table: { defaultProps: { verticalSpacing: 'xs', horizontalSpacing: 'sm', fontSize: 'sm' } },
    TextInput: { defaultProps: { size: 'sm' } },
    Select: { defaultProps: { size: 'sm' } },
    MultiSelect: { defaultProps: { size: 'sm' } },
    Checkbox: { defaultProps: { size: 'sm' } },
    Button: { defaultProps: { size: 'sm' } },
  },

  other: {
    /**
     * Tabular numerals for queue dates and codes (07 §1): columns of figures
     * must line up vertically to be scannable at a glance.
     */
    tabularNumbers: 'font-variant-numeric: tabular-nums;',
  },
})

/**
 * Surface colors Mantine composes `dimmed` against — its `--mantine-color-body`
 * in each scheme. Named here so the contrast assertions in `theme.test.ts` test
 * the real pairing rather than a hardcoded guess.
 */
export const BODY_LIGHT = '#ffffff'
export const BODY_DARK = '#242424' // --mantine-color-dark-7

/**
 * The `Fieldset variant="filled"` surface, in each scheme.
 *
 * Named because it is the **lowest-contrast backdrop `dimmed` binds to in this
 * app**, not a hypothetical one: the facility confirmation block is a filled
 * `Fieldset` (`FacilityAddressBlock.tsx`) and every one of its five labels is a
 * `dimmed` `<dt>` sitting on it (`ReadOnlyField.tsx`). Measured in Chromium by
 * `e2e/disabled-surfaces.spec.ts`, the old `DIMMED_LIGHT` painted **4.65** here
 * against 4.90 on the body — the whole app's floor, 0.15 above failing, and it
 * was invisible to every gate because the token had only ever been checked
 * against `--mantine-color-body`.
 */
export const FILLED_SURFACE_LIGHT = '#f8f9fa' // --mantine-color-gray-0
export const FILLED_SURFACE_DARK = '#2e2e2e' // --mantine-color-dark-6

/**
 * Accessible replacements for Mantine's `dimmed`, which **fails WCAG 2.2 AA in
 * both schemes** (measured 2026-07-29 against @mantine/core 9.5):
 *
 * | scheme | Mantine default        | on body   | ratio     |
 * |--------|------------------------|-----------|-----------|
 * | light  | `gray-6`  `#868e96`    | `#ffffff` | **3.32**  |
 * | dark   | `dark-2`  `#828282`    | `#242424` | **4.03**  |
 *
 * Both are under the 4.5 AA needs for normal text, and `dimmed` is not a rare
 * decoration — it carries timestamps, hint text and secondary labels across the
 * queue and the workspace. Rule 8 makes AA non-negotiable, so the token is
 * overridden rather than the requirement waived.
 *
 * The replacements keep a visible step below body text (`dark-0` `#c9c9c9` at
 * 9.37 in dark) so "dimmed" still reads as dimmed — the point is a legible
 * hierarchy, not a flat one.
 *
 * **`DIMMED_LIGHT` was re-derived 2026-08-08 (Stage 3's surface audit).** The
 * 2026-07-29 value (`#69727a`) was chosen against `--mantine-color-body` alone,
 * and that was the only surface anything checked. Stage 2 then made `dimmed`
 * carry the read-only envelope's field labels, and the audit measured them in
 * Chromium at **4.65** on the filled `Fieldset` behind the facility block
 * (`FILLED_SURFACE_LIGHT`) — the lowest ratio in the app, clearing AA by 0.15.
 * Every other token in this file is aimed at ~5.5 for exactly this reason: a
 * Mantine palette nudge, a `variant="filled"` on one more panel, or a slightly
 * darker surface would have taken it under with no test objecting. Chosen now
 * against its **lowest-contrast binding surface** rather than its most common
 * one, which is the same correction `PLACEHOLDER_LIGHT` needed in 1.0.9.
 *
 * `DIMMED_DARK` is unchanged: the same audit measured it at 5.79 on the body and
 * **5.07** on the filled `Fieldset`, both clear of AA with real headroom, and
 * lightening it would cost hierarchy against `#c9c9c9` body text for nothing.
 * The two schemes differ because their surfaces do; symmetry is not a reason.
 *
 * **Known, out of scope, and not fixed here:** `Table striped highlightOnHover`
 * (`QueueTable.tsx:235`) paints a hovered row at `gray-1` in light and `dark-5`
 * in dark, and the queue puts `dimmed` text on those rows. By `contrast.ts`
 * arithmetic the new `DIMMED_LIGHT` clears it (5.23 on `#f1f3f5`) but
 * `DIMMED_DARK` does **not** (4.18 on `#3b3b3b`). That is a table surface, not a
 * disabled one, and it wants its own measurement in a real browser and its own
 * plan entry rather than a token nudge smuggled in here (Stage 3, "scope
 * creep").
 */
export const DIMMED_LIGHT = '#5f666d' // 5.82 on #ffffff, 5.52 on #f8f9fa
export const DIMMED_DARK = '#9e9e9e' // 5.79 on #242424, 5.07 on #2e2e2e

/**
 * The empty-cell dash in the queue table, dark scheme only — resolves the
 * "known, out of scope" note above (07 §4, measured 2026-08-09).
 *
 * **Why this is not a `DIMMED_DARK` change.** `DIMMED_DARK` clears AA on every
 * surface it binds to except one, and that one is not its fault: a hovered
 * `Table striped highlightOnHover` row paints `#3b3b3b`, the lightest backdrop
 * a row ever has, where `#9e9e9e` lands at **4.18**. Lightening the token would
 * fix one em dash by flattening `dimmed` against `#c9c9c9` body text
 * everywhere else — the trade the comment above declined, and still declines.
 *
 * **Why it can be scoped to one component at all.** `Absent()`
 * (`QueueTable.tsx`) is the only `dimmed` text a row renders; every other run
 * in a hovered row was measured well clear (the job link 11.77, `<time>` 6.76).
 * So the failing pairing is reachable from exactly one place, and fixing it
 * there costs nothing anywhere else.
 *
 * **`#aeaeae` paints 5.05 on `#3b3b3b`**, and 5.0 is the deliberate target
 * rather than the ~5.5 the disabled tokens use — the same call, for the same
 * reason, that `theme.test.ts` already records for `DIMMED_DARK`: headroom
 * bought past that point is paid for in hierarchy. Body text on this very row
 * is only 6.76, so a dash at 5.5 would sit close enough to read as ordinary
 * text rather than as an absence marker. 5.05 is comfortably clear of the 4.5
 * floor without closing that gap. On the darker row states it only rises (7.00
 * on the body, 6.12 on `#2e2e2e`).
 *
 * The residual risk this accepts: the backdrop is Mantine's
 * (`--mantine-color-table-hover`), so a palette nudge could move it, and 0.55
 * of headroom is less than the disabled tokens carry. That is what the gate is
 * for — `e2e/table-contrast.spec.ts` measures the painted row every run, so a
 * nudge fails a build rather than shipping.
 *
 * Light is deliberately untouched: `DIMMED_LIGHT` already paints 5.23 on the
 * hovered light row (`#f1f3f5`). The two schemes differ because their surfaces
 * do; symmetry is not a reason.
 */
export const ABSENT_DARK = '#aeaeae' // 5.05 on #3b3b3b (hovered row), 7.00 on #242424

/**
 * Applied by `MantineProvider` (01 §2 providers). A CSS-variable override is the
 * right seam: `dimmed` is consumed as `var(--mantine-color-dimmed)` by Mantine's
 * own components as well as ours, so patching the variable fixes every use at
 * once — including the ones inside Mantine we do not author.
 */
/**
 * Same story, bigger blast radius: **every `variant="light"` badge hue we use
 * fails AA in the light scheme.** Measured in real Chrome 2026-07-29 by
 * `e2e/badge-contrast.spec.ts`, after both the axe suite (no computed styles in
 * jsdom) and Lighthouse (audits `/login`, which has no badges) passed all of
 * them:
 *
 * | hue    | Mantine text | on light-variant bg | ratio    | replacement        |
 * |--------|--------------|---------------------|----------|--------------------|
 * | teal   | `#087f5b`    | `#c3fae8`           | **4.33** | `#076c4d` → 5.57   |
 * | blue   | `#1971c2`    | `#d0ebff`           | **4.07** | `#155a99` → 5.76   |
 * | orange | `#d9480f`    | `#ffe8cc`           | **3.62** | `#a8380c` → 5.46   |
 * | yellow | `#e67700`    | `#fff3bf`           | **2.69** | `#7a4100` → 7.29   |
 *
 * These are not decorations: they are the queue's open / in-progress / awaiting-QA
 * totals (04 §3) and the live-connection badge (04 §3b).
 *
 * Yellow is the interesting one — a hue that light leaves almost no luminance
 * range above its own background, so the replacement reads as a dark amber
 * rather than a yellow. That is the honest trade: the badge states
 * "Reconnecting…" in words (07 §4 forbids colour-only status anyway), so
 * legibility wins over hue fidelity.
 *
 * Each replacement is aimed at ~5.5 rather than a bare 4.5 so a future Mantine
 * palette tweak does not silently drop back under. **Only the light scheme needs
 * this** — dark measures 7:1 or better throughout and is left alone.
 *
 * Token overrides, not per-component `c=` props: every current *and future* light
 * badge inherits the fix instead of re-discovering the bug.
 */
export const LIGHT_VARIANT_TEXT: Readonly<Record<string, string>> = {
  teal: '#076c4d', // 5.57 on #c3fae8
  blue: '#155a99', // 5.76 on #d0ebff
  orange: '#a8380c', // 5.46 on #ffe8cc
  yellow: '#7a4100', // 7.29 on #fff3bf
}

/**
 * The surfaces a disabled control is composed against — `--mantine-color-disabled`
 * in each scheme, and since 2026-08-08 **ours rather than Mantine's**. Named so
 * the assertions in `theme.test.ts` test the real pairing rather than a
 * hardcoded guess.
 *
 * **Dark moved from `#2e2e2e` to `#262626` (Stage 3, Task 3).** Mantine ships
 * `--mantine-color-disabled: dark-6` and `--input-bg: dark-6` — the identical
 * hex — so a disabled input and an editable one had **no painted background
 * difference at all** once Stage 1 removed the `opacity: 0.6` that had been
 * separating them (1.057 → exactly 1.000, measured). Light never had that
 * problem: `#e9ecef` on a `#ffffff` field is a real step at 1.186.
 *
 * `#262626` restores a step of **1.114** — comparable to light's — and, because
 * it is darker, simultaneously *raises* every disabled-text ratio in dark rather
 * than trading legibility for affordance. That is why this is the lever the
 * audit chose over re-tuning text colour alone: text colour can only move one of
 * the two, and moving it toward "obviously unavailable" moves it away from
 * "readable" (`e2e/disabled-surfaces.spec.ts` asserts the step in both schemes).
 *
 * Light is overridden to the value Mantine already computes. A no-op today, on
 * purpose: it makes `DISABLED_BG_LIGHT` **true by construction** instead of true
 * by coincidence, so `theme.test.ts`'s arithmetic and the browser's paint cannot
 * drift apart the next time Mantine touches `gray-2`.
 */
export const DISABLED_BG_LIGHT = '#e9ecef' // was --mantine-color-gray-2
export const DISABLED_BG_DARK = '#262626' // was --mantine-color-dark-6 (#2e2e2e)

/**
 * The **editable** input surface and the text on it, in dark.
 *
 * `DISABLED_BG_DARK` used to double as both, which is precisely the confusion
 * that hid the missing affordance: as long as one constant meant "disabled
 * background" and "the background an editable field has", nothing could state
 * the difference between them, let alone assert it.
 */
export const INPUT_BG_DARK = '#2e2e2e' // --mantine-color-dark-6
export const EDITABLE_TEXT_DARK = '#c9c9c9' // --mantine-color-text in dark

/**
 * Accessible replacements for Mantine's `disabled` and `placeholder` text,
 * which **fail WCAG 2.2 AA in both schemes** (measured in real Chrome against
 * staging 2026-08-07, after admins reported the data was unreadable):
 *
 * | pairing                       | scheme | Mantine default        | ratio    |
 * |-------------------------------|--------|-------------------------|----------|
 * | disabled value                | light  | `#adb5bd` on `#e9ecef` | **1.75** |
 * | disabled value                | dark   | `#696969` on `#2e2e2e` | **2.47** |
 * | placeholder, on enabled bg    | light  | `#adb5bd` on `#ffffff` | **2.07** |
 * | placeholder, on disabled bg   | light  | `#adb5bd` on `#e9ecef` | **1.75** |
 * | placeholder                   | dark   | `#696969` on `#2e2e2e` | **2.47** |
 *
 * 1.75 does not even clear the 3:1 floor for non-text. **A disabled field here
 * is not an inactive control** — WCAG exempts those from contrast, but this
 * field is the only place the transcribed envelope is displayed to an admin
 * reviewing a job they cannot edit (`WorkspaceScreen` passes
 * `disabled={!canEdit}` to every region). It is data, so it is held to AA.
 *
 * Mantine ships **one value for both tokens**, so an empty field waiting for
 * input and a filled field carrying real mail were styled identically. They are
 * separated here because they sit on different surfaces: a placeholder is on the
 * enabled input background, a disabled value on the disabled one.
 *
 * **A field can be disabled AND empty at once** — Address One/Two are optional
 * (`SenderFields.tsx`) and Facility is a `Select` (`FacilitySearch.tsx`), so a
 * `shipped` job with a blank optional field renders its `placeholder` straight
 * onto the *disabled* background, not the enabled one (reviewer finding,
 * 2026-08-07 — the first cut of `PLACEHOLDER_LIGHT` only checked the enabled
 * surface and cleared AA there at 5.57 while sitting at **4.70** on `#e9ecef`,
 * 4% of headroom from the 4.5 floor with no test watching it). So
 * `PLACEHOLDER_LIGHT` is chosen against **both** surfaces it can actually
 * render on, not just the more common one.
 *
 * Each is aimed at ~5.5 on its *lowest*-contrast binding surface rather than a
 * bare 4.5, matching `LIGHT_VARIANT_TEXT`, so an upstream palette tweak cannot
 * silently drop back under. All four stay a visible step below body text
 * (21:1 light, 9.37:1 dark) so "unavailable" still reads as unavailable.
 *
 * `PLACEHOLDER_LIGHT` now equals `DISABLED_TEXT_LIGHT` — **by arithmetic, not
 * intent.** `#e9ecef` is the binding (lower-contrast) surface for both, and
 * 5.5 is the target for both, so they landed on the same hex. They stay two
 * separate constants: they are different tokens composed against different
 * surface sets (placeholder also has to clear `#ffffff`, which disabled-value
 * never touches), and collapsing them into one name would hide that a future
 * surface change could make them diverge again without anything objecting.
 *
 * **The dark-scheme cost (reviewer finding, 2026-08-07).** Mantine's dark
 * disabled and enabled *input* backgrounds are the identical hex (`#2e2e2e`),
 * so in dark the text colour is the only affordance separating a disabled
 * field from an editable one — there is no background step to fall back on.
 * That separation fell from **3.315** (`#696969` disabled-default vs `#c9c9c9`
 * editable-value text, both read as text-on-text) to **1.488**
 * (`DISABLED_TEXT_DARK` vs `#c9c9c9`) after this fix, because the replacement
 * was chosen for AA against the *background*, not for staying visually distinct
 * from editable text. It remains shippable: the field is not focusable, carries
 * `cursor: not-allowed`, and 07 §4 already forbids conveying status by colour
 * alone — but it is a real cost of the fix, previously unstated.
 *
 * **A token is a declared ratio, not a painted one (2026-08-08).** Every number
 * above is the token pair's arithmetic, and for the first day of 1.0.9 that was
 * all anybody checked. Mantine paints a disabled input at `opacity: 0.6`, so
 * the 5.55/5.51 below reached admins' screens as **2.43** and **2.96** and the
 * reported bug survived its own fix. `inputs.css` — imported at the top of this
 * file — restores `opacity: 1` on the `Input` base class, and the ratios below
 * are now what real Chromium paints (re-measured 2026-08-08 on disabled
 * `TextInput`, `Autocomplete` and `Select` in both schemes). Anything added here
 * in future is a *declared* ratio until an element-level property has been ruled
 * out; `e2e/form-contrast.spec.ts` is where that gets settled.
 *
 * **The dark affordance got worse, and is Stage 3's call, not this file's.**
 * Removing the wash removed the only painted background difference dark had:
 * disabled and enabled input backgrounds are the same `#2e2e2e`, which the 0.6
 * used to separate to a (barely visible) 1.057 and now separates to exactly
 * **1.000**. Text-on-text fell the same way — the painted disabled value stood
 * at 2.831 against `#c9c9c9` editable text and now stands at the **1.488**
 * recorded above. Light is unaffected: `#e9ecef` vs `#ffffff` is a real step
 * (1.186) and the value text still separates at 3.189 from editable black.
 * Legibility is the requirement (HARD RULE 8) and "which field can I type in"
 * is not carried by colour alone — the field is unfocusable and cursors
 * `not-allowed` — so this ships, but it is a genuine regression in the
 * *unavailable* signal and it is recorded rather than absorbed.
 *
 * **Surface headroom for the next person who adds a disabled surface.**
 * Neither replacement clears AA against every Mantine shade — each was chosen
 * for the two surfaces it actually binds to in this app, not for universal
 * safety: `DISABLED_TEXT_DARK` (`#a5a5a5`) clears `dark-5` (`#3b3b3b`) by only
 * **0.05** (4.55) and **fails outright on `dark-4`** (`#424242`, 4.08).
 * `DISABLED_TEXT_LIGHT`/`PLACEHOLDER_LIGHT` (`#5d5d5d`) fail on any surface
 * darker than `#d6d6d6`, including Mantine's own **`gray-4`** (`#ced4da`,
 * 4.41). A future disabled surface lighter/darker than `disabled`'s current
 * `#262626`/`gray-2` needs its own measurement, not an assumption that these
 * hexes travel.
 *
 * ---
 *
 * **Stage 3 (2026-08-08): what the audit changed, and what it did not.**
 *
 * The disabled-surface audit (`e2e/disabled-surfaces.spec.ts`) enumerated every
 * disabled surface this app renders and measured each as painted. Two things
 * came out of it that this file has to hold.
 *
 * **1. A disabled field can still carry data, so the AA floor stays.** Stage 2
 * moved the read-only envelope to static text, and the plan for this stage
 * expected that to free `DISABLED_TEXT_DARK` to be re-derived against the 3:1
 * *non-text* floor — the right bar for a control nobody can act on. It is not
 * free: `WorkspaceScreen`'s `wasEditable` latch keeps the **form** on a panel
 * that has ever been editable, so a claim collision (03 §4) or a completed
 * submit leaves the transcriber's own typed envelope sitting in disabled inputs,
 * and the Unknown-Sender lock freezes a populated sender block on every job.
 * Those are data. Both tokens therefore keep the ~5.5 target, and the audit
 * asserts them at 4.5 in a real browser.
 *
 * **2. The affordance was bought from the background instead.** With the floor
 * unmovable, `DISABLED_BG_DARK` is where the separation came from (see its own
 * comment): `#262626` puts a **1.114** step back between a disabled field and an
 * editable one, and lifts every disabled-text pairing in dark at the same time.
 * That headroom then paid for a *second* improvement — `DISABLED_TEXT_DARK`
 * moved `#a5a5a5` → `#9c9c9c`, which is the darkest value still clearing 5.5 on
 * the new background (5.51) and takes the disabled-vs-editable **text**
 * separation from **1.488** to **1.658**. Not the 3.315 of the pre-1.0.9
 * default, which was only ever available at 2.47:1 legibility — but the most the
 * AA floor allows, which is the honest ceiling for this trade.
 *
 * `PLACEHOLDER_DARK` deliberately does **not** follow it. A placeholder is
 * guidance in a field the user can type in — never an inactive control — and it
 * binds to the *enabled* input background (`INPUT_BG_DARK`, still `#2e2e2e`),
 * where `#9c9c9c` would measure 4.95 and lose the headroom. So the dark pair
 * separates at last, and the two constants stop sharing a hex "by arithmetic,
 * not intent". In **light** the coincidence survives: `DISABLED_TEXT_LIGHT` did
 * not move (see below), so `#5d5d5d` is still right for both.
 *
 * **Light was left alone, on the same evidence.** It already has the background
 * step dark had lost (`#e9ecef` vs `#ffffff`, 1.186), its disabled value still
 * separates from editable black text at 3.19, and it paints 5.55. There is
 * nothing for a change to buy, and every hex here is a surface somebody has to
 * re-measure.
 */
export const DISABLED_TEXT_LIGHT = '#5d5d5d' // 5.55 on #e9ecef
export const DISABLED_TEXT_DARK = '#9c9c9c' // 5.51 on #262626; 1.658 vs editable #c9c9c9
export const PLACEHOLDER_LIGHT = '#5d5d5d' // 6.58 on #ffffff, 5.55 on #e9ecef
export const PLACEHOLDER_DARK = '#a5a5a5' // 5.51 on #2e2e2e (the ENABLED input bg)

export const cssVariablesResolver: CSSVariablesResolver = () => ({
  variables: {},
  light: {
    '--mantine-color-dimmed': DIMMED_LIGHT,
    '--mantine-color-disabled': DISABLED_BG_LIGHT,
    '--mantine-color-disabled-color': DISABLED_TEXT_LIGHT,
    '--mantine-color-placeholder': PLACEHOLDER_LIGHT,
    /**
     * `--pm-*` is ours, not Mantine's: this names a surface Mantine has no
     * token for (the empty-cell dash, `ABSENT_DARK`). Light deliberately
     * resolves to plain `dimmed` — it already clears AA on the hovered row, so
     * there is nothing to override and one fewer hex to re-measure.
     */
    '--pm-color-absent': DIMMED_LIGHT,
    ...Object.fromEntries(
      Object.entries(LIGHT_VARIANT_TEXT).map(([hue, value]) => [
        `--mantine-color-${hue}-light-color`,
        value,
      ]),
    ),
  },
  dark: {
    '--mantine-color-dimmed': DIMMED_DARK,
    /**
     * The one token here that is a **surface**, not text: it is what gives a
     * disabled field in dark any painted difference from an editable one at all
     * (see `DISABLED_BG_DARK`). Overridden at the variable rather than per
     * component so every disabled control — input, button, checkbox box —
     * moves together; a per-component fix would have left the same gap open
     * somewhere else, which is how this family of bugs keeps recurring.
     */
    '--mantine-color-disabled': DISABLED_BG_DARK,
    '--mantine-color-disabled-color': DISABLED_TEXT_DARK,
    '--mantine-color-placeholder': PLACEHOLDER_DARK,
    /**
     * The one place a `--pm-*` value diverges from `dimmed`, and the only
     * scheme that needs it — see `ABSENT_DARK`. Scoped to a variable rather
     * than written into `QueueTable.tsx` so the hex stays in this file with
     * the rest of the measured palette, where `theme.test.ts` can assert it.
     */
    '--pm-color-absent': ABSENT_DARK,
  },
})
